.row {
	height: 12.5cqh;
	display: grid;
	grid-template-columns: 56px 1fr;
	gap: var(--space-3);
	padding: 0 var(--space-4);
	border-bottom: 1px solid var(--border);
	box-sizing: border-box;
	align-items: center;
}

.row:last-child {
	border-bottom: none;
}

/* When a recipe is linked, reserve a right-hand column for the go-to-recipe
   button so it lands in the same spot in both editable and read-only rows. */
.hasRecipe {
	grid-template-columns: 56px 1fr auto;
}

.label {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 1px;
	line-height: 1.2;
}

.weekday {
	font-size: 0.6875rem;
	font-weight: 600;
	color: var(--ink-muted);
	text-transform: uppercase;
	letter-spacing: 0.04em;
}

.date {
	font-size: 0.8125rem;
	font-weight: 500;
}

.today .weekday,
.today .date {
	color: var(--accent-sage);
}

.body {
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 1px;
	min-width: 0;
	line-height: 1.2;
}

/* Structural reset only — typography (size/weight/colour) is governed by
   `.title` / `.note` below so the inputs and the read-only text stay in sync.
   The global `input { font: inherit }` still supplies the body font. */
input.title,
input.note {
	border: none;
	background: transparent;
	padding: 0;
	outline: none;
	width: 100%;
	line-height: 1.2;
}

input.title::placeholder,
input.note::placeholder {
	color: var(--ink-muted);
	font-weight: 400;
	opacity: 0.55;
}

/* One line each, always. The row is a fixed fraction of the column's height
   and sized for two lines of text, so a long meal name or note that wrapped
   would spill over the rows either side of it. The inputs can't wrap; the
   read-only divs that replace them can. */
.title,
.note {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* Meal name — semibold, full-contrast ink on editable days. */
.title {
	font-size: 1rem;
	font-weight: 600;
}

/* Note — secondary line. Compact 14px at rest (read-only days and idle editable
   rows alike), a touch lighter than plain muted so it reads below the meal name. */
.note {
	font-size: 0.875rem;
	font-weight: 400;
	color: color-mix(in srgb, var(--ink-muted) 80%, transparent);
}

/* Grow to 16px only while the note is actively edited, so iOS Safari doesn't
   zoom in on focus. (A flat 16px is the guaranteed no-zoom fix; this focus swap
   keeps idle rows compact but relies on Safari honouring the focused 16px —
   less certain across iOS versions, so verify on device.) */
input.note:focus {
	font-size: 1rem;
}

/* Past days are read-only: fade both lines so a finished day reads as inactive
   and is visibly distinct from the editable days above it. */
.readonly .title {
	color: var(--ink-muted);
}

.readonly .note {
	color: color-mix(in srgb, var(--ink-muted) 50%, transparent);
}

.placeholder {
	color: var(--ink-muted);
	opacity: 0.45;
}

/* Wraps the title input + its anchored suggestion list. `position: relative`
   anchors the absolutely-positioned dropdown to this box. */
.titleField {
	position: relative;
	display: flex;
	align-items: center;
	gap: var(--space-2);
}

.titleField input.title {
	flex: 1;
	min-width: 0;
}

.gotoRecipe {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	color: var(--accent-sage);
	border-radius: var(--radius-md);
	flex-shrink: 0;
	text-decoration: none;
	transition: background 120ms ease;
}

a.gotoRecipe:hover {
	background: var(--surface-input);
}

.suggestions {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	margin: 4px 0 0;
	padding: 4px 0;
	list-style: none;
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: var(--radius-md);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	z-index: 10;
}

.suggestion {
	display: flex;
	align-items: center;
	gap: var(--space-2);
	width: 100%;
	padding: var(--space-2) var(--space-3);
	background: transparent;
	border: none;
	font: inherit;
	font-size: 0.8125rem;
	color: var(--ink);
	text-align: left;
	cursor: pointer;
}

.suggestion:hover {
	background: var(--surface-input);
}

.suggestionIcon {
	color: var(--accent-sage);
	flex-shrink: 0;
}

.suggestionTitle {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
	flex: 1;
}

/* Select mode. A leading column holds the check circle, and the row itself is
   the tap target — the inputs and the recipe link are gone. */
.selectRow {
	grid-template-columns: 24px 56px 1fr;
}

.selectRow.hasRecipe {
	grid-template-columns: 24px 56px 1fr auto;
}

/* Undo the button UA styles, keeping only the divider `.row` draws. */
button.selectRow {
	font: inherit;
	color: inherit;
	text-align: left;
	background: none;
	border-width: 0 0 1px;
	border-style: solid;
	border-color: var(--border);
	cursor: pointer;
}

.check {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: var(--ink-muted);
	opacity: 0.55;
}

.selected {
	background: color-mix(in srgb, var(--accent-sage) 12%, transparent);
}

.selected .check {
	color: var(--accent-sage);
	opacity: 1;
}

/* Present, but not a link while selecting. */
.selectRow .gotoRecipe {
	color: var(--ink-muted);
	opacity: 0.5;
}

/* A suggested meal, not saved yet. Lilac rather than the sage of a ticked
   row, so a proposal never reads as something already written down. */
.proposed {
	background: color-mix(in srgb, var(--accent-lilac) 16%, transparent);
}

.proposed .check {
	color: var(--accent-lilac);
	opacity: 1;
}

.proposed .gotoRecipe {
	color: var(--accent-lilac);
	opacity: 1;
}
